import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Footer from '@/components/feature/Footer';
import SignupRequiredModal from '@/components/feature/SignupRequiredModal';
import DualCurrencyMoney from '@/components/base/DualCurrencyMoney';
import i18n, { resolveHtmlLang } from '@/i18n';
import { getSupabaseClient } from '@/lib/supabase';
import { getProductDetail } from '@/lib/finishingPreviewService';
import type { ProductDetailData } from '@/lib/finishingPreviewService';
import { previewQuote, createQuote, QuoteApiError, quoteErrorMessage } from '@/lib/quotationService';
import type { QuotePreviewRequest, CustomerPreviewData } from '@/lib/quotationTypes';
import {
  loadFinishingMethodLookup,
  resolveFinishingMethod,
  type CanonicalFinishingMethod,
} from '@/lib/finishingMethodResolver';
import { mockTargets, customizationTree } from '@/mocks/customizationMenu';
import type { CustomizationTarget, ProcessType, AddedProcess } from '@/mocks/customizationMenu';
import CustomizationTree from '@/pages/customize-quote/components/CustomizationTree';
import ProcessSection from '@/pages/customize-quote/components/ProcessSection';
import {
  getSnapshot,
  resetSnapshot,
  savePageState,
  clearSnapshot,
} from '@/lib/customizeQuoteStore';
import { useCurrency } from '@/hooks/useCurrency';

let processIdCounter = 0;
function nextProcessId(): string {
  processIdCounter += 1;
  return `proc-${processIdCounter}`;
}

export default function CustomizeQuotePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { selectedFinishingIds?: string[]; orderQuantity?: number; sampleQuantity?: number } | null;
  const passedOrderQuantity = locationState?.orderQuantity ?? 0;
  const passedSampleQuantity = locationState?.sampleQuantity ?? 0;
  const locationKey = location.key;

  const { t } = useTranslation('customize');

  // ─── Editable quantities ───
  const [orderQty, setOrderQty] = useState(passedOrderQuantity);
  const [sampleQty, setSampleQty] = useState(passedSampleQuantity);

  // ─── Preview / Create state ───
  const [previewData, setPreviewData] = useState<CustomerPreviewData | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // ─── Document locale (official quote language) ───
  // Authority = current UI language. Preserved across Preview → Create so a
  // mid-session language switch never changes the quote's issued language.
  const documentLocaleRef = useRef<string>(resolveHtmlLang(i18n.language));

  // ─── Quote currency (official quote currency) ───
  // Authority = selected currency preference (useCurrency). Preserved across
  // Preview → Create so a mid-session currency switch never changes the quote
  // after preview. Independent from documentLocale — no currency→locale inference.
  const { currency } = useCurrency();
  const quoteCurrencyRef = useRef<string>(currency);

  // ─── Auth gate ───
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function checkAuth() {
      const supabase = getSupabaseClient();
      if (!supabase) return;
      const { data: { user } } = await supabase.auth.getUser();
      if (cancelled) return;
      setIsLoggedIn(!!user);
    }
    checkAuth();
    return () => { cancelled = true; };
  }, []);

  // ─── Product ───
  const [product, setProduct] = useState<ProductDetailData | null>(null);
  const [productLoading, setProductLoading] = useState(true);

  // ─── Targets ───
  const [targets, setTargets] = useState<CustomizationTarget[]>([]);
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null);

  // ─── Processes ───
  // Restore from store when returning from the full-screen image viewer (same location key).
  const [addedProcesses, setAddedProcesses] = useState<AddedProcess[]>(() => {
    const snap = getSnapshot();
    return snap.locationKey === locationKey ? snap.addedProcesses : [];
  });
  // confirmed state per process: processId -> any (for future quote generation)
  const [confirmedProcessData, setConfirmedProcessData] = useState<Record<string, unknown>>(() => {
    const snap = getSnapshot();
    return snap.locationKey === locationKey ? snap.confirmedData : {};
  });

  // On a fresh entry (different location key), reset the stored snapshot.
  useEffect(() => {
    const snap = getSnapshot();
    if (snap.locationKey !== locationKey) {
      resetSnapshot(locationKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist page state so it survives the image-viewer round-trip.
  useEffect(() => {
    savePageState(locationKey, addedProcesses, confirmedProcessData);
  }, [locationKey, addedProcesses, confirmedProcessData]);

  // ─── Fetch product & build targets ───
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setProductLoading(true);

    async function fetchProduct() {
      const data = await getProductDetail(id!);
      if (cancelled) return;
      setProduct(data);
      setProductLoading(false);

      // Build targets: current product + mock alternatives
      const currentTarget: CustomizationTarget = {
        id: `target-current-${data.id}`,
        name: data.name,
        sku: data.sku,
        imageUrl: data.images?.[0] || '',
      };
      setTargets([currentTarget, ...mockTargets]);
      setSelectedTargetId(currentTarget.id);
    }

    fetchProduct();
    return () => { cancelled = true; };
  }, [id]);

  // ─── Add process from tree menu ───
  const handleAddProcess = useCallback((menuId: string, labelKey: string, processType: ProcessType) => {
    if (!isLoggedIn) {
      setShowSignupModal(true);
      return;
    }
    const newProcess: AddedProcess = {
      id: nextProcessId(),
      processType,
      title: labelKey,
      labelKey,
    };
    setAddedProcesses((prev) => [...prev, newProcess]);
  }, [isLoggedIn]);

  // ─── Remove process ───
  const handleRemoveProcess = useCallback((processId: string) => {
    setAddedProcesses((prev) => prev.filter((p) => p.id !== processId));
    setConfirmedProcessData((prev) => {
      const next = { ...prev };
      delete next[processId];
      return next;
    });
  }, []);

  // ─── Process confirmed ───
  const handleProcessConfirmed = useCallback((processId: string, data: unknown) => {
    setConfirmedProcessData((prev) => ({ ...prev, [processId]: data }));
  }, []);

  const selectedTarget = targets.find((t) => t.id === selectedTargetId);

  // ─── Build quote preview request from confirmed selections ───
  type BuildRequestResult =
    | { ok: true; request: QuotePreviewRequest }
    | { ok: false; error: string };

  const buildPreviewRequest = useCallback(async (): Promise<BuildRequestResult> => {
    if (!product) {
      return { ok: false, error: t('customize.error.loadingProduct') };
    }

    const allSelections = Object.values(confirmedProcessData).flat() as Array<{
      methodId?: string;
      methodCode?: string;
      sourceType?: 'finish_l1' | 'print';
      colorName?: string | null;
    }>;

    // Only priced selections (finish_l1 | print) enter the finishing array.
    // Non-quote UI options (parts_replace, color_only) are intentionally excluded.
    const pricedSelections = allSelections.filter(
      (s) => s.sourceType === 'finish_l1' || s.sourceType === 'print',
    );

    const finishings: Array<{ finishingMethodId: string; pantoneColor: string | null }> = [];

    if (pricedSelections.length > 0) {
      let lookup: Map<string, CanonicalFinishingMethod>;
      try {
        lookup = await loadFinishingMethodLookup();
      } catch {
        return {
          ok: false,
          error: t('customize.error.loadingFinishing'),
        };
      }

      for (const s of pricedSelections) {
        const method = resolveFinishingMethod(lookup, s.sourceType, s.methodCode);
        if (!method) {
          // Diagnostic identity only — never guess by name, never send a UI id.
          console.warn('[Quote] Missing finishing mapping', {
            sourceType: s.sourceType,
            sourceCode: s.methodCode,
          });
          return {
            ok: false,
            error: t('customize.error.finishingMapping'),
          };
        }
        finishings.push({
          finishingMethodId: method.id,
          pantoneColor: s.colorName || null,
        });
      }
    }

    const effectiveQuantity = sampleQty > 0 ? sampleQty : Math.max(1, orderQty);
    const quoteType: 'production' | 'sample' = sampleQty > 0 ? 'sample' : 'production';

    return {
      ok: true,
      request: {
        quoteType,
        quoteCurrency: quoteCurrencyRef.current,
        documentLocale: documentLocaleRef.current,
        items: [
          {
            productId: product.id,
            quantity: effectiveQuantity,
            finishings,
          },
        ],
        specialRequirements: null,
      },
    };
  }, [product, confirmedProcessData, sampleQty, orderQty]);

  const handlePreviewQuote = async () => {
    if (!isLoggedIn) {
      setShowSignupModal(true);
      return;
    }
    // Capture the current UI language as the official document locale for this
    // quote-building session (preview + subsequent create share the same value).
    documentLocaleRef.current = resolveHtmlLang(i18n.language);
    // Capture the current selected currency as the official quote currency for
    // this quote-building session (preview + subsequent create share the same value).
    quoteCurrencyRef.current = currency;
    const built = await buildPreviewRequest();
    if (!built.ok) {
      setPreviewError(built.error);
      setShowPreviewModal(true);
      return;
    }
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewData(null);
    setShowPreviewModal(true);
    try {
      const data = await previewQuote(built.request);
      setPreviewData(data);
    } catch (err) {
      if (err instanceof QuoteApiError) {
        setPreviewError(quoteErrorMessage(err.code, err.message));
      } else if (err instanceof Error) {
        setPreviewError(err.message);
      } else {
        setPreviewError(t('customize.error.preview'));
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCreateQuote = async () => {
    const built = await buildPreviewRequest();
    if (!built.ok) {
      setCreateError(built.error);
      return;
    }
    setCreateLoading(true);
    setCreateError(null);
    try {
      const result = await createQuote(built.request);
      setShowPreviewModal(false);
      navigate(`/my/quotes/${result.quoteId}`);
    } catch (err) {
      if (err instanceof QuoteApiError) {
        setCreateError(quoteErrorMessage(err.code, err.message));
      } else if (err instanceof Error) {
        setCreateError(err.message);
      } else {
        setCreateError(t('customize.error.create'));
      }
    } finally {
      setCreateLoading(false);
    }
  };

  // ─── Loading ───
  if (productLoading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="flex items-center justify-center py-40">
          <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-[3px] border-warm-200 border-t-warm-500 rounded-full animate-spin" />
            <p className="text-sm text-ink-400">{t('customize.loadingProduct')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white">
        <div className="flex items-center justify-center py-40">
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="w-14 h-14 rounded-full bg-rose-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-rose-500 text-2xl" />
            </div>
            <h3 className="text-lg font-bold text-ink-700">{t('customize.productNotFound')}</h3>
            <button
              onClick={() => navigate('/packaging')}
              className="px-5 py-2.5 text-sm font-bold text-white bg-ink-700 rounded-lg hover:bg-ink-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('customize.backToProductList')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const productImage = product.images?.[0] || '';

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Top Bar */}
      <div className="sticky top-0 z-30 bg-white border-b border-ink-100">
        <div className="w-full px-4 md:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-7 h-7 rounded-lg bg-warm-100 flex items-center justify-center shrink-0">
              <i className="ri-brush-line text-warm-500 text-sm" />
            </div>
            <div className="hidden sm:block min-w-0">
              <h1 className="text-base font-extrabold text-ink-700 truncate">{t('customize.title')}</h1>
            </div>
            {selectedTarget && (
              <span className="hidden md:flex items-center gap-1 text-xs text-ink-400">
                <span className="text-ink-200">|</span>
                <span className="truncate max-w-[160px]">{selectedTarget.name}</span>
              </span>
            )}
          </div>
          <button
            onClick={() => {
              clearSnapshot();
              navigate(`/products/${id}`);
            }}
            className="px-3 py-1.5 text-xs font-medium text-ink-500 border border-ink-200 rounded-lg hover:bg-cream-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
          >
            <i className="ri-arrow-left-line" />
            {t('customize.backToProduct')}
          </button>
        </div>
      </div>

      {/* Main Content: Sidebar + Process Area */}
      <div className="flex-1 flex">
        {/* ─── Left Sidebar ─── */}
        <aside className="w-64 xl:w-72 shrink-0 border-r border-ink-100 bg-cream-50/20 flex flex-col h-[calc(100vh-57px)] sticky top-[57px]">
          {/* Tree Menu */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {selectedTargetId ? (
              <CustomizationTree
                items={customizationTree}
                onAddProcess={handleAddProcess}
                disabled={!isLoggedIn}
              />
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-4 text-center">
                <i className="ri-stack-line text-3xl text-ink-200 mb-3" />
                <p className="text-xs text-ink-400">{t('customize.selectTargetHint')}</p>
              </div>
            )}
          </div>
        </aside>

        {/* ─── Main Process Area ─── */}
        <main className="flex-1 min-w-0 overflow-y-auto">
          <div className="w-full px-4 md:px-6 py-6 md:py-8 space-y-6">
            {/* Selected Target Info */}
            {selectedTarget && (() => {
              const isCurrentProduct = selectedTargetId?.startsWith('target-current-') && product;
              const weight = isCurrentProduct ? (product.specifications.find((s) => s.label === '무게 (g)')?.value || '') : '';

              const specItems: { icon: string; label: string; value: string; color: string }[] = [];
              if (isCurrentProduct) {
                if (product.categoryL1) {
                  specItems.push({
                    icon: 'ri-folder-line',
                    label: t('customize.category'),
                    value: product.categoryL2 ? `${product.categoryL1} › ${product.categoryL2}` : product.categoryL1,
                    color: 'text-accent-700',
                  });
                }
                if (product.capacity && product.capacity !== 'N/A') {
                  specItems.push({ icon: 'ri-drop-line', label: t('customize.capacity'), value: product.capacity, color: 'text-secondary-700' });
                }
                if (product.material && product.material !== 'N/A') {
                  specItems.push({ icon: 'ri-test-tube-line', label: t('customize.material'), value: product.material, color: 'text-primary-700' });
                }
                if (weight) {
                  specItems.push({ icon: 'ri-scales-3-line', label: t('customize.weight'), value: `${weight}g`, color: 'text-warm-600' });
                }
              }

              return (
                <div className="bg-white rounded-xl border border-ink-100 overflow-hidden">
                  <div className="flex items-stretch">
                    {/* Image */}
                    <div className="w-[120px] md:w-[144px] shrink-0 bg-cream-50/60 border-r border-ink-100 flex items-center justify-center p-3">
                      <img
                        src={selectedTarget.imageUrl || productImage}
                        alt={selectedTarget.name}
                        className="w-full h-full object-contain"
                        style={{ maxHeight: 120 }}
                      />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0 p-4">
                      {/* Header row */}
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="min-w-0">
                          <h2 className="text-sm md:text-[15px] font-extrabold text-ink-800 leading-snug">{selectedTarget.name}</h2>
                        </div>
                        {addedProcesses.length > 0 && (
                          <span className="shrink-0 px-2.5 py-1 rounded-full bg-warm-100 text-warm-600 text-[10px] font-bold whitespace-nowrap">
                            {t('customize.processCount', { count: addedProcesses.length })}
                          </span>
                        )}
                      </div>

                      {/* Divider */}
                      <div className="border-t border-ink-100 mb-3" />

                      {/* Spec grid */}
                      <div className="flex flex-wrap items-start gap-x-5 gap-y-2">
                        {/* SKU — always shown */}
                        <div className="flex items-center gap-1.5 shrink-0">
                          <div className="w-4 h-4 flex items-center justify-center shrink-0">
                            <i className="ri-barcode-line text-ink-400 text-xs" />
                          </div>
                          <div>
                            <p className="text-[9px] text-ink-400 leading-none mb-0.5">{t('customize.sku')}</p>
                            <p className="text-[11px] font-semibold text-ink-700 whitespace-nowrap">{selectedTarget.sku}</p>
                          </div>
                        </div>

                        {/* Dynamic spec items */}
                        {specItems.map((item) => (
                          <div key={item.label} className="contents">
                            <div className="w-px h-7 bg-ink-100 shrink-0 self-center" />
                            <div className="flex items-center gap-1.5 shrink-0">
                              <div className="w-4 h-4 flex items-center justify-center shrink-0">
                                <i className={`${item.icon} text-ink-400 text-xs`} />
                              </div>
                              <div>
                                <p className="text-[9px] text-ink-400 leading-none mb-0.5">{item.label}</p>
                                <p className={`text-[11px] font-semibold whitespace-nowrap ${item.color}`}>{item.value}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Editable Quantity Controls */}
                      <div className="border-t border-ink-100 mt-3 pt-3">
                        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                          {/* Order Quantity */}
                          <div className="flex items-center gap-2.5">
                            <span className="text-sm font-bold text-ink-600">{t('customize.orderQuantity')}</span>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setOrderQty((p) => Math.max(1, p - 1))}
                                disabled={orderQty <= 1}
                                className="w-7 h-7 flex items-center justify-center rounded-md border border-ink-200 text-ink-500 hover:bg-cream-50 hover:text-ink-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                              >
                                <i className="ri-subtract-line text-xs" />
                              </button>
                              <input
                                type="number"
                                value={orderQty}
                                onChange={(e) => {
                                  const v = parseInt(e.target.value, 10);
                                  if (!isNaN(v) && v >= 1) setOrderQty(v);
                                }}
                                className="w-16 h-7 text-center text-sm font-bold text-ink-700 border border-ink-200 rounded-md focus:outline-none focus:border-warm-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              />
                              <button
                                onClick={() => setOrderQty((p) => p + 1)}
                                className="w-7 h-7 flex items-center justify-center rounded-md border border-ink-200 text-ink-500 hover:bg-cream-50 hover:text-ink-700 transition-colors cursor-pointer"
                              >
                                <i className="ri-add-line text-xs" />
                              </button>
                            </div>
                            <span className="text-xs text-ink-400">{t('customize.unit')}</span>
                          </div>

                          {/* Sample Quantity */}
                          {sampleQty > 0 && (
                          <div className="flex items-center gap-2.5">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-extrabold border border-amber-300 whitespace-nowrap">
                              <i className="ri-shopping-bag-3-line text-xs" />
                              {t('customize.samplePurchase')}
                            </span>
                            <span className="text-sm font-bold text-amber-700">{t('customize.sampleQuantity')}</span>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setSampleQty((p) => Math.max(0, p - 1))}
                                disabled={sampleQty <= 0}
                                className="w-7 h-7 flex items-center justify-center rounded-md border border-ink-200 text-ink-500 hover:bg-cream-50 hover:text-ink-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                              >
                                <i className="ri-subtract-line text-xs" />
                              </button>
                              <input
                                type="number"
                                value={sampleQty}
                                onChange={(e) => {
                                  const v = parseInt(e.target.value, 10);
                                  if (!isNaN(v) && v >= 0 && v <= 50) setSampleQty(v);
                                }}
                                className="w-16 h-7 text-center text-sm font-bold text-amber-700 border border-amber-200 rounded-md focus:outline-none focus:border-amber-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              />
                              <button
                                onClick={() => setSampleQty((p) => Math.min(50, p + 1))}
                                disabled={sampleQty >= 50}
                                className="w-7 h-7 flex items-center justify-center rounded-md border border-ink-200 text-ink-500 hover:bg-cream-50 hover:text-ink-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                              >
                                <i className="ri-add-line text-xs" />
                              </button>
                            </div>
                            <span className="text-xs text-ink-400">{t('customize.unit')}</span>
                          </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Empty State */}
            {addedProcesses.length === 0 && (
              <div className="py-8 flex flex-col items-start gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-cream-50 border border-ink-100 flex items-center justify-center">
                    <i className="ri-git-branch-line text-lg text-ink-300" />
                  </div>
                  <h3 className="text-sm font-bold text-ink-600">{t('customize.addProcessTitle')}</h3>
                </div>
                <p className="text-sm text-ink-400 leading-relaxed">{t('customize.addProcessDesc')}</p>
              </div>
            )}

            {/* Process Sections */}
            {addedProcesses.map((proc, idx) => (
              <div key={proc.id}>
                {idx > 0 && (
                  <div className="flex items-center gap-2 mb-5">
                    <span className="text-[10px] font-bold text-ink-400 uppercase tracking-wider shrink-0">{t('customize.processStep', { index: idx + 1 })}</span>
                    <div className="flex-1 h-px bg-ink-100" />
                  </div>
                )}
                <ProcessSection
                  processId={proc.id}
                  processType={proc.processType}
                  title={proc.title}
                  labelKey={proc.labelKey}
                  index={idx}
                  productId={product.id}
                  productImage={productImage}
                  workingImageId={product.workingImageId}
                  productName={selectedTarget?.name || product.name}
                  categoryL1={product.categoryL1}
                  categoryL2={product.categoryL2}
                  outerDiameterMm={product.outerDiameterMm}
                  innerDiameterMm={product.innerDiameterMm}
                  heightMm={product.heightMm}
                  onRemove={handleRemoveProcess}
                  onConfirmed={handleProcessConfirmed}
                  collapseOnConfirm={proc.processType !== 'finishing_color' && proc.processType !== 'finishing_color_completed' && proc.processType !== 'finishing_only'}
                />
              </div>
            ))}

            {/* Quote Output Button */}
              <div className="pt-4 pb-6">
                <div className="flex items-center gap-2 mb-5">
                  <span className="text-[10px] font-bold text-ink-400 uppercase tracking-wider shrink-0">{t('customize.finalStep')}</span>
                  <div className="flex-1 h-px bg-ink-100" />
                </div>

                <div className="flex items-center justify-center gap-4">
                  <button
                    onClick={() => {
                      clearSnapshot();
                      setAddedProcesses([]);
                      setConfirmedProcessData({});
                      navigate(`/products/${id}`);
                    }}
                    className="px-6 py-2.5 text-sm font-medium text-ink-500 border border-ink-200 rounded-xl hover:bg-cream-50 hover:text-ink-700 transition-all cursor-pointer whitespace-nowrap"
                  >
                    {t('customize.cancel')}
                  </button>
                  <button
                    onClick={handlePreviewQuote}
                    className="px-7 py-2.5 bg-ink-700 text-white text-sm font-bold rounded-xl hover:bg-ink-600 transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"
                  >
                    <i className="ri-file-text-line" />
                    {t('customize.printQuote')}
                  </button>
                </div>
              </div>
          </div>
        </main>
      </div>

      <Footer />

      {/* ─── Preview / Create Modal ─── */}
      {showPreviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => !createLoading && setShowPreviewModal(false)} />
          <div className="relative z-10 mx-4 w-full max-w-[520px] bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200 max-h-[85vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-ink-100 bg-cream-50/50 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-warm-100 flex items-center justify-center">
                  <i className="ri-file-text-line text-warm-500 text-sm" />
                </div>
                <h3 className="text-sm font-bold text-ink-700">{t('customize.previewTitle')}</h3>
              </div>
              <button
                onClick={() => !createLoading && setShowPreviewModal(false)}
                className="w-7 h-7 rounded-lg hover:bg-ink-100 flex items-center justify-center cursor-pointer text-ink-400 hover:text-ink-600 transition-colors"
              >
                <i className="ri-close-line" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 overflow-y-auto flex-1">
              {previewLoading && (
                <div className="flex flex-col items-center justify-center py-10 gap-3">
                  <div className="w-8 h-8 border-2 border-warm-200 border-t-warm-500 rounded-full animate-spin" />
                  <p className="text-sm text-ink-500 font-medium">{t('customize.calculating')}</p>
                </div>
              )}

              {!previewLoading && previewError && (
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-5 text-center">
                  <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-2">
                    <i className="ri-error-warning-line text-rose-500 text-lg" />
                  </div>
                  <p className="text-sm text-rose-600 font-medium">{previewError}</p>
                </div>
              )}

              {!previewLoading && previewData && (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-ink-500">{t('customize.currency')}</span>
                    <span className="font-bold text-ink-700">{previewData.currency.code}</span>
                  </div>
                  {previewData.fx && previewData.fx.appliedFxRate && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-ink-500">{t('customize.appliedFx')}</span>
                      <span className="font-bold text-ink-700">1 USD = {previewData.fx.appliedFxRate} {previewData.currency.code}</span>
                    </div>
                  )}

                  {/* Items */}
                  <div className="border-t border-ink-100 pt-3 space-y-3">
                    {previewData.items.map((item) => (
                      <div key={item.productId} className="bg-cream-50/50 rounded-lg p-3 border border-ink-100">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-sm font-bold text-ink-700">{item.product.name}</p>
                          {item.product.sku && (
                            <span className="text-[10px] font-mono text-ink-400">{item.product.sku}</span>
                          )}
                        </div>
                        <p className="text-xs text-ink-400 mb-2">{t('customize.quantityLabel', { count: item.quantity.toLocaleString() })}</p>
                        {item.finishings.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {item.finishings.map((f) => (
                              <span key={f.finishingMethodId || f.methodCode} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-warm-50 text-warm-600 text-[10px] font-medium border border-warm-200">
                                {f.methodName}
                                {f.pantoneColor && ` · ${f.pantoneColor}`}
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-ink-500">{t('customize.itemTotal')}</span>
                          <DualCurrencyMoney
                            usdAmount={item.itemTotalUsd}
                            selectedAmount={item.itemTotal}
                            selectedCurrency={previewData.currency.code}
                            primaryClassName="font-bold text-ink-700"
                            secondaryClassName="text-xs font-normal text-ink-400"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Totals */}
                  <div className="border-t border-ink-100 pt-3 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-ink-500">{t('customize.productSubtotalUsd')}</span>
                      <DualCurrencyMoney
                        usdAmount={previewData.totals.productSubtotalUsd}
                        selectedAmount={null}
                        selectedCurrency={previewData.totals.quoteCurrency}
                        primaryClassName="font-medium text-ink-700"
                        secondaryClassName="text-xs font-normal text-ink-400"
                      />
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-ink-500">{t('customize.finishingSubtotalUsd')}</span>
                      <DualCurrencyMoney
                        usdAmount={previewData.totals.finishingSubtotalUsd}
                        selectedAmount={null}
                        selectedCurrency={previewData.totals.quoteCurrency}
                        primaryClassName="font-medium text-ink-700"
                        secondaryClassName="text-xs font-normal text-ink-400"
                      />
                    </div>
                    <div className="border-t border-ink-100 pt-2 mt-2 flex items-center justify-between">
                      <span className="text-base font-bold text-ink-700">{t('customize.totalQuoteAmount')}</span>
                      <DualCurrencyMoney
                        usdAmount={previewData.totals.baseTotalUsd}
                        selectedAmount={previewData.totals.quoteTotal}
                        selectedCurrency={previewData.totals.quoteCurrency}
                        primaryClassName="text-lg font-extrabold text-warm-600"
                        secondaryClassName="text-xs font-normal text-ink-400"
                      />
                    </div>
                  </div>

                  {previewData.warnings.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1">
                      {previewData.warnings.map((w, i) => (
                        <p key={i} className="text-xs text-amber-700 flex items-start gap-1">
                          <i className="ri-alert-line shrink-0 mt-0.5" />
                          {w.message}
                        </p>
                      ))}
                    </div>
                  )}

                  {createError && (
                    <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-600 flex items-center gap-2">
                      <i className="ri-error-warning-line" />
                      {createError}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer actions */}
            <div className="px-6 py-4 border-t border-ink-100 bg-cream-50/30 shrink-0">
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={() => setShowPreviewModal(false)}
                  disabled={createLoading}
                  className="px-4 py-2 text-xs font-bold text-ink-600 border border-ink-200 rounded-lg hover:bg-white transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {t('customize.close')}
                </button>
                {previewData && (
                  <button
                    onClick={handleCreateQuote}
                    disabled={createLoading}
                    className="px-5 py-2 text-xs font-bold text-white bg-warm-500 rounded-lg hover:bg-warm-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5 disabled:opacity-50"
                  >
                    {createLoading ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        {t('customize.processing')}
                      </>
                    ) : (
                      <>
                        <i className="ri-check-line" />
                        {t('customize.requestQuote')}
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <SignupRequiredModal
        open={showSignupModal}
        onClose={() => setShowSignupModal(false)}
      />
    </div>
  );
}